import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  GripVertical,
  Trash2,
  Search,
  RefreshCw,
  AlignLeft,
  Pencil,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  MessageSquare,
  Lock,
  Archive,
  ArchiveRestore,
  Share2,
  Link,
  Copy,
  Eye,
  Users,
  Paperclip,
  Upload,
  Smartphone,
  Mail,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../hooks/useAuth.js';
import { useEntityLabels } from '../hooks/useEntityLabels.js';
import { formatPhone } from '../utils/phone.js';
import {
  getQuote as fetchQuote,
  createQuote,
  updateQuote,
  addLineItems,
  updateLineItem,
  deleteLineItem,
  reorderLineItems,
  refreshQuotePrices,
  listResellers,
  createReseller,
  listResellerContacts,
  listCatalogs,
  searchProducts,
  getProductGroups,
  getTenantSettings,
  getSentStatus,
  createShareLink,
  getShareLinks,
  getAttachments,
  uploadAttachment,
  deleteAttachment,
  getAttachmentDownloadUrl,
  emailQuote,
  smsQuote,
  getCollateralItems,
  getQuoteEmailLog,
  ApiError,
} from '../api/client.js';
import type { QuoteDetail, QuoteLineItem, Reseller, ResellerContact, Catalog, Product, TenantSettings, SentStatus, ShareLink, QuoteAttachment, CollateralItem, QuoteEmail } from '../api/client.js';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function pct(decimal: string | number): string {
  const n = typeof decimal === 'string' ? parseFloat(decimal) : decimal;
  return `${Math.round(n * 10000) / 100}`;
}

function fromPct(pctStr: string): number {
  return parseFloat(pctStr) / 100;
}

function calcResellerEach(
  unitPrice: number,
  resellerDiscount: number,
  projectDiscount: number,
  applyProjectDiscounts: boolean,
  projectDiscountOverride: string | null,
  quoteProjectDiscount: number,
): number {
  const effProj = applyProjectDiscounts
    ? (projectDiscountOverride !== null ? parseFloat(projectDiscountOverride) : quoteProjectDiscount)
    : 0;
  return unitPrice * (1 - resellerDiscount) * (1 - effProj);
}

function isValidEmail(email: string): boolean {
  if (!email) return true;
  const atIdx = email.indexOf('@');
  return atIdx > 0 && email.indexOf('.', atIdx) > atIdx + 1;
}

function getValidUntilHelper(dateStr: string): string {
  if (!dateStr) return '';
  const target = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = target.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day from today';
  if (diffDays > 1) return `${diffDays} days from today`;
  if (diffDays === -1) return '1 day ago';
  return `${Math.abs(diffDays)} days ago`;
}

// ── Sortable Row ─────────────────────────────────────────────────────────────

interface SortableRowProps {
  item: QuoteLineItem;
  index: number;
  resellerDiscount: number;
  quoteProjectDiscount: number;
  isReseller: boolean;
  targetMargin: number;
  readOnly: boolean;
  onUpdateItem: (id: string, data: Record<string, unknown>) => void;
  onDeleteItem: (id: string) => void;
}

function SortableRow({
  item,
  index,
  resellerDiscount,
  quoteProjectDiscount,
  isReseller,
  targetMargin,
  readOnly,
  onUpdateItem,
  onDeleteItem,
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [editingQty, setEditingQty] = useState(false);
  const [qtyValue, setQtyValue] = useState(String(item.quantity ?? 1));
  const [editingDisc, setEditingDisc] = useState(false);
  const [discValue, setDiscValue] = useState(
    item.projectDiscountOverride !== null ? pct(item.projectDiscountOverride) : '',
  );
  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState(item.descriptionText ?? '');
  const [editingMargin, setEditingMargin] = useState(false);
  const [marginValue, setMarginValue] = useState(
    item.marginOverride !== null ? pct(item.marginOverride) : '',
  );
  const [editingCatDisc, setEditingCatDisc] = useState(false);
  const [catDiscValue, setCatDiscValue] = useState(
    item.discountSnapshot ? pct(item.discountSnapshot) : '0',
  );

  const unitPrice = parseFloat(item.unitPrice ?? '0');
  const qty = item.quantity ?? 1;
  const applyProj = item.applyProjectDiscounts !== false;
  const resellerEach = calcResellerEach(
    unitPrice,
    resellerDiscount,
    quoteProjectDiscount,
    applyProj,
    item.projectDiscountOverride,
    quoteProjectDiscount,
  );

  // Reseller pricing: cost + margin -> sell price
  const costEach = resellerEach;
  const effMargin = item.marginOverride !== null ? parseFloat(item.marginOverride) : targetMargin;
  const sellEach = effMargin > 0 && effMargin < 1 ? costEach / (1 - effMargin) : costEach;

  const saveQty = () => {
    setEditingQty(false);
    const num = parseInt(qtyValue, 10);
    if (!isNaN(num) && num >= 1 && num !== qty) {
      onUpdateItem(item.id, { quantity: num });
    } else {
      setQtyValue(String(qty));
    }
  };

  const saveDisc = () => {
    setEditingDisc(false);
    if (discValue.trim() === '') {
      onUpdateItem(item.id, { projectDiscountOverride: null });
    } else {
      const num = parseFloat(discValue);
      if (!isNaN(num) && num >= 0 && num <= 100) {
        onUpdateItem(item.id, { projectDiscountOverride: num / 100 });
      }
    }
  };

  const saveMargin = () => {
    setEditingMargin(false);
    if (marginValue.trim() === '') {
      onUpdateItem(item.id, { marginOverride: null });
    } else {
      const num = parseFloat(marginValue);
      if (!isNaN(num) && num >= 0 && num < 100) {
        onUpdateItem(item.id, { marginOverride: num / 100 });
      }
    }
  };

  const saveCatDisc = () => {
    setEditingCatDisc(false);
    const num = parseFloat(catDiscValue);
    if (!isNaN(num) && num >= 0 && num <= 100) {
      onUpdateItem(item.id, { discountSnapshot: num / 100 });
    }
  };

  const saveDesc = () => {
    setEditingDesc(false);
    if (descValue !== (item.descriptionText ?? '')) {
      onUpdateItem(item.id, { descriptionText: descValue });
    }
  };

  if (item.isDescriptionRow) {
    const descColSpan = 5;
    const trailingColSpan = isReseller ? 2 : 3;
    return (
      <tr ref={setNodeRef} style={style} className="border-b border-gray-100 bg-blue-50/30">
        <td className="w-8 px-2 py-2 text-center">
          <button {...(readOnly ? {} : { ...attributes, ...listeners })} className={readOnly ? 'text-gray-300' : 'cursor-grab text-gray-400 hover:text-gray-600'}>
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
        <td className="px-2 py-2 text-xs text-gray-400">{index + 1}</td>
        <td colSpan={descColSpan} className="px-2 py-2">
          {editingDesc && !readOnly ? (
            <input
              type="text"
              value={descValue}
              onChange={(e) => setDescValue(e.target.value)}
              onBlur={saveDesc}
              onKeyDown={(e) => { if (e.key === 'Enter') saveDesc(); }}
              autoFocus
              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-brand-500 focus:outline-none"
            />
          ) : (
            <button
              onClick={() => !readOnly && setEditingDesc(true)}
              className={`w-full text-left text-sm italic text-gray-600 ${readOnly ? '' : 'hover:text-gray-900'}`}
            >
              {item.descriptionText || (readOnly ? '\u2014' : 'Click to add description...')}
            </button>
          )}
        </td>
        <td colSpan={trailingColSpan} />
        <td className="w-8 px-2 py-2 text-center">
          {!readOnly && (
            <button
              onClick={() => onDeleteItem(item.id)}
              className="text-gray-400 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </td>
      </tr>
    );
  }

  // Reseller tenant: simplified 8-column layout with per-line catalog discount
  if (isReseller) {
    const discount = parseFloat(item.discountSnapshot ?? '0');
    const rCostEach = unitPrice * (1 - discount);
    const rSellEach = effMargin > 0 && effMargin < 1 ? rCostEach / (1 - effMargin) : rCostEach;

    return (
      <tr ref={setNodeRef} style={style} className="border-b border-gray-100 hover:bg-gray-50">
        <td className="w-8 px-2 py-2 text-center">
          <button {...(readOnly ? {} : { ...attributes, ...listeners })} className={readOnly ? 'text-gray-300' : 'cursor-grab text-gray-400 hover:text-gray-600'}>
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
        <td className="px-2 py-2 text-xs text-gray-400">{index + 1}</td>
        <td className="px-2 py-2 text-sm font-medium text-gray-900">{item.partNumber || ''}</td>
        <td className="max-w-[200px] truncate px-2 py-2 text-sm text-gray-600">
          {item.description || ''}
        </td>
        <td className="w-16 px-2 py-2 text-center">
          {editingQty && !readOnly ? (
            <input
              type="number"
              min="1"
              value={qtyValue}
              onChange={(e) => setQtyValue(e.target.value)}
              onBlur={saveQty}
              onKeyDown={(e) => { if (e.key === 'Enter') saveQty(); }}
              autoFocus
              className="w-16 rounded border border-gray-300 bg-white px-1 py-0.5 text-center text-sm text-gray-900 focus:border-brand-500 focus:outline-none"
            />
          ) : (
            <button
              onClick={() => !readOnly && setEditingQty(true)}
              className={`w-full text-center text-sm text-gray-900 ${readOnly ? '' : 'hover:bg-gray-100'}`}
            >
              {qty}
            </button>
          )}
        </td>
        <td className="px-2 py-2 text-right">
          <div className="text-sm font-medium text-gray-900">{formatCurrency(rCostEach)}</div>
          <div className="text-xs text-gray-400">
            {editingCatDisc && !readOnly ? (
              <span>
                MSRP {formatCurrency(unitPrice)} &middot;{' '}
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={catDiscValue}
                  onChange={(e) => setCatDiscValue(e.target.value)}
                  onBlur={saveCatDisc}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveCatDisc(); }}
                  autoFocus
                  className="w-14 rounded border border-gray-300 bg-white px-1 text-center text-xs text-gray-700 focus:border-brand-500 focus:outline-none"
                />
                % disc.
              </span>
            ) : (
              <button
                onClick={() => {
                  if (readOnly) return;
                  setCatDiscValue(pct(discount));
                  setEditingCatDisc(true);
                }}
                className={readOnly ? '' : 'hover:text-gray-600'}
              >
                MSRP {formatCurrency(unitPrice)}
                {discount > 0 && ` \u00b7 ${pct(discount)}% disc.`}
              </button>
            )}
          </div>
        </td>
        <td className="w-20 px-2 py-2 text-center">
          {editingMargin && !readOnly ? (
            <input
              type="number"
              min="0"
              max="99.99"
              step="0.1"
              value={marginValue}
              onChange={(e) => setMarginValue(e.target.value)}
              onBlur={saveMargin}
              onKeyDown={(e) => { if (e.key === 'Enter') saveMargin(); }}
              autoFocus
              className="w-16 rounded border border-gray-300 bg-white px-1 py-0.5 text-center text-sm text-gray-900 focus:border-brand-500 focus:outline-none"
            />
          ) : (
            <button
              onClick={() => {
                if (readOnly) return;
                setMarginValue(
                  item.marginOverride !== null
                    ? pct(item.marginOverride)
                    : targetMargin > 0 ? pct(targetMargin) : '',
                );
                setEditingMargin(true);
              }}
              className={`text-sm ${readOnly ? '' : 'hover:bg-gray-100'} ${
                item.marginOverride !== null ? 'font-medium text-brand-700' : 'text-gray-700'
              }`}
            >
              {effMargin > 0 ? `${pct(effMargin)}%` : '\u2014'}
            </button>
          )}
        </td>
        <td className="px-2 py-2 text-right text-sm text-gray-700">
          {formatCurrency(rSellEach)}
        </td>
        <td className="px-2 py-2 text-right text-sm font-medium text-gray-900">
          {formatCurrency(rSellEach * qty)}
        </td>
        <td className="w-8 px-2 py-2 text-center">
          {!readOnly && (
            <button
              onClick={() => onDeleteItem(item.id)}
              className="text-gray-400 hover:text-red-500"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </td>
      </tr>
    );
  }

  // Manufacturer layout
  const effProjDisc = applyProj
    ? (item.projectDiscountOverride !== null
        ? parseFloat(item.projectDiscountOverride)
        : quoteProjectDiscount)
    : 0;
  const displayDisc = item.projectDiscountOverride !== null
    ? pct(item.projectDiscountOverride)
    : pct(quoteProjectDiscount);

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-gray-100 hover:bg-gray-50">
      <td className="w-8 px-2 py-2 text-center">
        <button {...(readOnly ? {} : { ...attributes, ...listeners })} className={readOnly ? 'text-gray-300' : 'cursor-grab text-gray-400 hover:text-gray-600'}>
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="px-2 py-2 text-xs text-gray-400">{index + 1}</td>
      <td className="px-2 py-2 text-xs text-gray-500">{item.groupName || ''}</td>
      <td className="px-2 py-2 text-sm font-medium text-gray-900">{item.partNumber || ''}</td>
      <td className="max-w-[200px] truncate px-2 py-2 text-sm text-gray-600">
        {item.description || ''}
      </td>
      <td className="w-16 px-2 py-2 text-center">
        {editingQty && !readOnly ? (
          <input
            type="number"
            min="1"
            value={qtyValue}
            onChange={(e) => setQtyValue(e.target.value)}
            onBlur={saveQty}
            onKeyDown={(e) => { if (e.key === 'Enter') saveQty(); }}
            autoFocus
            className="w-16 rounded border border-gray-300 bg-white px-1 py-0.5 text-center text-sm text-gray-900 focus:border-brand-500 focus:outline-none"
          />
        ) : (
          <button
            onClick={() => !readOnly && setEditingQty(true)}
            className={`w-full text-center text-sm text-gray-900 ${readOnly ? '' : 'hover:bg-gray-100'}`}
          >
            {qty}
          </button>
        )}
      </td>
      <td className="px-2 py-2 text-right text-sm text-gray-700">
        {formatCurrency(unitPrice)}
      </td>
      <td className="w-20 px-2 py-2 text-center">
        <div className="text-sm text-gray-700">{pct(resellerDiscount)}%</div>
        {applyProj && effProjDisc > 0 ? (
          editingDisc && !readOnly ? (
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={discValue}
              onChange={(e) => setDiscValue(e.target.value)}
              onBlur={saveDisc}
              onKeyDown={(e) => { if (e.key === 'Enter') saveDisc(); }}
              autoFocus
              className="mt-0.5 w-16 rounded border border-gray-300 bg-white px-1 py-0.5 text-center text-xs text-gray-500 focus:border-brand-500 focus:outline-none"
            />
          ) : (
            <button
              onClick={() => {
                if (readOnly) return;
                setDiscValue(
                  item.projectDiscountOverride !== null
                    ? pct(item.projectDiscountOverride)
                    : pct(quoteProjectDiscount),
                );
                setEditingDisc(true);
              }}
              className={`text-xs italic text-gray-400 ${readOnly ? '' : 'hover:bg-gray-100'}`}
            >
              +{displayDisc}%
            </button>
          )
        ) : null}
      </td>
      <td className="px-2 py-2 text-right text-sm text-gray-700">
        {formatCurrency(resellerEach)}
      </td>
      <td className="px-2 py-2 text-right text-sm font-medium text-gray-900">
        {formatCurrency(resellerEach * qty)}
      </td>
      <td className="w-8 px-2 py-2 text-center">
        {!readOnly && (
          <button
            onClick={() => onDeleteItem(item.id)}
            className="text-gray-400 hover:text-red-500"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  );
}

// ── Product Search Modal ─────────────────────────────────────────────────────

interface ProductSearchModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (productIds: string[]) => void;
}

function ProductSearchModal({ open, onClose, onAdd }: ProductSearchModalProps) {
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [results, setResults] = useState<(Product & { catalogName: string })[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [catalogFilter, setCatalogFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSearchTerm('');
    setCatalogFilter('');
    setGroupFilter('');
    setResults([]);

    listCatalogs()
      .then((d) => setCatalogs(d.catalogs.filter((c) => c.active)))
      .catch(() => {});
    getProductGroups()
      .then((d) => setGroups(d.groups))
      .catch(() => {});
  }, [open]);

  const doSearch = useCallback(
    async (search: string, catalogId: string, group: string) => {
      setLoading(true);
      try {
        const data = await searchProducts({
          search: search || undefined,
          catalogIds: catalogId ? [catalogId] : undefined,
          groupName: group || undefined,
          limit: 100,
        });
        setResults(data.products);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(searchTerm, catalogFilter, groupFilter);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, catalogFilter, groupFilter, open, doSearch]);

  const toggleProduct = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === results.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(results.map((p) => p.id)));
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex h-[80vh] w-full max-w-4xl flex-col rounded-lg border border-gray-800 bg-gray-900">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-800 p-6 pb-4">
          <h2 className="mb-4 text-lg font-semibold text-gray-100">Add Products to Quote</h2>

          {/* Search controls */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search part number, description, group..."
                className="w-full rounded-lg border border-gray-700 bg-gray-800 py-2 pl-9 pr-4 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                autoFocus
              />
            </div>
            <select
              value={catalogFilter}
              onChange={(e) => setCatalogFilter(e.target.value)}
              className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">All Catalogs</option>
              {catalogs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {groups.length > 0 && (
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="">All Groups</option>
                {groups.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
            </div>
          ) : results.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              {searchTerm || catalogFilter || groupFilter
                ? 'No products found.'
                : 'Search or browse products to add them.'}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="sticky top-0 bg-gray-800/80 text-left text-xs uppercase tracking-wider text-gray-400">
                  <th className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={results.length > 0 && selected.size === results.length}
                      onChange={toggleAll}
                      className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-brand-600 focus:ring-brand-500"
                    />
                  </th>
                  <th className="px-3 py-2">Group</th>
                  <th className="px-3 py-2">Part Number</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2 text-right">MSRP</th>
                  <th className="px-3 py-2">Catalog</th>
                </tr>
              </thead>
              <tbody>
                {results.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => toggleProduct(p.id)}
                    className={`cursor-pointer border-b border-gray-800 ${
                      selected.has(p.id) ? 'bg-brand-900/20' : 'hover:bg-gray-800/30'
                    }`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(p.id)}
                        onChange={() => toggleProduct(p.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-brand-600 focus:ring-brand-500"
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-400">{p.groupName || '\u2014'}</td>
                    <td className="px-3 py-2 text-sm font-medium text-gray-100">
                      {p.partNumber}
                    </td>
                    <td className="max-w-[200px] truncate px-3 py-2 text-sm text-gray-300">
                      {p.description || '\u2014'}
                    </td>
                    <td className="px-3 py-2 text-right text-sm text-gray-100">
                      {formatCurrency(parseFloat(p.msrp))}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-400">{p.catalogName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 justify-end gap-3 border-t border-gray-800 p-6 pt-4">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onAdd([...selected]);
              onClose();
            }}
            disabled={selected.size === 0}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            Add Selected ({selected.size})
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Status Selector Dropdown ──────────────────────────────────────────────────

function StatusSelector({
  status,
  onChange,
}: {
  status: string;
  onChange: (newStatus: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const options = [
    { value: 'draft', label: 'Draft', icon: Pencil, iconClass: 'text-gray-400' },
    { value: 'ready', label: 'Ready', icon: Lock, iconClass: 'text-amber-400' },
    { value: 'finalize', label: 'Finalize', icon: Lock, iconClass: 'text-green-400' },
  ];

  const current = options.find((o) => o.value === status) || options[0]!;
  const CurrentIcon = current.icon;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md border border-gray-600 bg-gray-700 px-3 py-1 text-sm text-gray-200 hover:bg-gray-600"
      >
        <CurrentIcon className={`h-3.5 w-3.5 ${current.iconClass}`} />
        {current.label}
        <ChevronDown className="h-3 w-3 text-gray-500" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-40 rounded-md border border-gray-600 bg-gray-700 py-1 shadow-lg">
          {options.map((opt) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-600 ${
                  opt.value === status ? 'bg-gray-600/50' : ''
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${opt.iconClass}`} />
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main QuoteBuilderPage ────────────────────────────────────────────────────

export function QuoteBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const labels = useEntityLabels();
  const isReseller = user?.tenantType === 'reseller';
  const isNew = !id;

  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const [showProductModal, setShowProductModal] = useState(false);
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [resellerContactsList, setResellerContactsList] = useState<ResellerContact[]>([]);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [contactIsSearching, setContactIsSearching] = useState(false);

  // Custom reseller dropdown
  const [showResellerDropdown, setShowResellerDropdown] = useState(false);
  const [showNewResellerModal, setShowNewResellerModal] = useState(false);
  const [newResellerName, setNewResellerName] = useState('');
  const [newResellerDiscount, setNewResellerDiscount] = useState('0');
  const [newResellerSaving, setNewResellerSaving] = useState(false);
  const [newResellerError, setNewResellerError] = useState('');
  const resellerDropdownRef = useRef<HTMLDivElement>(null);

  // PDF download
  const [pdfLoading, setPdfLoading] = useState(false);

  // Quoted-by edit toggle
  const [editingQuotedBy, setEditingQuotedBy] = useState(false);

  // Email validation
  const [contactEmailError, setContactEmailError] = useState('');
  const [quotedByEmailError, setQuotedByEmailError] = useState('');

  // Refresh prices state
  const [refreshResult, setRefreshResult] = useState<{
    updated: number;
    changes: Array<{ partNumber: string; oldPrice: number; newPrice: number }>;
  } | null>(null);

  // Form state (for header fields)
  const [projectName, setProjectName] = useState('');
  const [resellerId, setResellerId] = useState('');
  const [resellerContact, setResellerContact] = useState('');
  const [resellerContactEmail, setResellerContactEmail] = useState('');
  const [resellerContactPhone, setResellerContactPhone] = useState('');
  const [quotedBy, setQuotedBy] = useState('');
  const [quotedByTitle, setQuotedByTitle] = useState('');
  const [quotedByEmail, setQuotedByEmail] = useState('');
  const [quotedByPhone, setQuotedByPhone] = useState('');
  const [resellerDiscountPct, setResellerDiscountPct] = useState('0');
  const [projectDiscountPct, setProjectDiscountPct] = useState('0');
  const [status, setStatus] = useState('draft');
  const [visibility, setVisibility] = useState('private');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [targetMarginPct, setTargetMarginPct] = useState('');
  const [boilerplateText, setBoilerplateText] = useState<string | null>(null);
  const [boilerplateOverride, setBoilerplateOverride] = useState(false);
  const [notes, setNotes] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [archived, setArchived] = useState(false);
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);

  // Collapsible sections
  const [quoteNotesOpen, setQuoteNotesOpen] = useState(true);
  const [boilerplateOpen, setBoilerplateOpen] = useState(false);

  // Tenant settings for boilerplate
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);

  // Sent status & share links
  const [sentRecipients, setSentRecipients] = useState<SentStatus[]>([]);
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLinkName, setShareLinkName] = useState('');
  const [shareLinkEmail, setShareLinkEmail] = useState('');
  const [shareLinkExpiry, setShareLinkExpiry] = useState('30');
  const [shareLinkCreating, setShareLinkCreating] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const isManufacturer = user?.tenantType === 'manufacturer';

  // Attachments
  const [attachments, setAttachments] = useState<QuoteAttachment[]>([]);
  const [showAttachments, setShowAttachments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  // Share dialog (combined email / sms / link)
  const [shareTab, setShareTab] = useState<'email' | 'sms' | 'link'>('email');
  const [smsPhone, setSmsPhone] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsError, setSmsError] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [emailName, setEmailName] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailIncludePdf, setEmailIncludePdf] = useState(true);
  const [emailCollateralIds, setEmailCollateralIds] = useState<string[]>([]);
  const [emailSending, setEmailSending] = useState(false);
  const [collateralLibrary, setCollateralLibrary] = useState<CollateralItem[]>([]);
  const [emailLog, setEmailLog] = useState<QuoteEmail[]>([]);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Load / Create ────────────────────────────────────────────────────────

  const loadResellers = useCallback(async () => {
    try {
      const data = await listResellers({ active: true });
      setResellers(data.resellers);
    } catch {
      // non-critical
    }
  }, []);

  const loadContacts = useCallback(async (rid: string) => {
    if (!rid) {
      setResellerContactsList([]);
      return;
    }
    try {
      const data = await listResellerContacts(rid);
      setResellerContactsList(data.contacts);
    } catch {
      setResellerContactsList([]);
    }
  }, []);

  const loadQuote = useCallback(
    async (quoteId: string) => {
      try {
        const data = await fetchQuote(quoteId);
        setQuote(data.quote);
        setProjectName(data.quote.projectName ?? '');
        setResellerId(data.quote.resellerId ?? '');
        setResellerContact(data.quote.resellerContact ?? '');
        setResellerContactEmail(data.quote.resellerContactEmail ?? '');
        setResellerContactPhone(data.quote.resellerContactPhone ?? '');
        setQuotedBy(data.quote.quotedBy ?? '');
        setQuotedByTitle(data.quote.quotedByTitle ?? '');
        setQuotedByEmail(data.quote.quotedByEmail ?? '');
        setQuotedByPhone(data.quote.quotedByPhone ?? '');
        setResellerDiscountPct(pct(data.quote.resellerDiscount));
        setProjectDiscountPct(pct(data.quote.projectDiscount));
        setTargetMarginPct(data.quote.targetMargin ? pct(data.quote.targetMargin) : '');
        setStatus(data.quote.status);
        setVisibility(data.quote.visibility ?? 'private');
        setQuoteNotes(data.quote.quoteNotes ?? '');
        setBoilerplateText(data.quote.boilerplateText ?? null);
        setBoilerplateOverride(data.quote.boilerplateOverride ?? false);
        setNotes(data.quote.notes ?? '');
        setValidUntil(data.quote.validUntil ?? '');
        setArchived(data.quote.archived ?? false);
        setSaveStatus('saved');
        if (data.quote.resellerId) {
          loadContacts(data.quote.resellerId);
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load quote');
      }
    },
    [loadContacts],
  );

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadResellers();
      getTenantSettings().then((d) => setTenantSettings(d.tenant)).catch(() => {});

      if (isNew) {
        try {
          const data = await createQuote({
            quotedBy: user?.name,
            quotedByEmail: user?.email,
            quotedByPhone: user?.phone ?? undefined,
          });
          setQuote(data.quote);
          setProjectName(data.quote.projectName ?? '');
          setResellerId(data.quote.resellerId ?? '');
          setQuotedBy(data.quote.quotedBy ?? '');
          setQuotedByTitle(data.quote.quotedByTitle ?? '');
          setQuotedByEmail(data.quote.quotedByEmail ?? '');
          setQuotedByPhone(data.quote.quotedByPhone ?? '');
          setResellerDiscountPct(pct(data.quote.resellerDiscount));
          setProjectDiscountPct(pct(data.quote.projectDiscount));
          setStatus(data.quote.status);
          setValidUntil(data.quote.validUntil ?? '');
          setArchived(data.quote.archived ?? false);
          navigate(`/quotes/${data.quote.id}`, { replace: true });
        } catch (err) {
          setError(err instanceof ApiError ? err.message : 'Failed to create quote');
        }
      } else {
        await loadQuote(id);
        // Load sent status + share links for existing quotes
        if (isManufacturer) {
          getSentStatus(id).then((d) => setSentRecipients(d.recipients)).catch(() => {});
        }
        getShareLinks(id).then((d) => setShareLinks(d.links)).catch(() => {});
        getAttachments(id).then((d) => setAttachments(d.attachments)).catch(() => {});
      }
      setLoading(false);
    };
    init();
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-save header fields ──────────────────────────────────────────────

  const saveHeaderField = useCallback(
    (field: string, value: unknown) => {
      if (!quote) return;
      setSaveStatus('saving');
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const data = await updateQuote(quote.id, { [field]: value });
          setQuote(data.quote);
          setSaveStatus('saved');
        } catch {
          setSaveStatus('unsaved');
        }
      }, 500);
    },
    [quote],
  );

  const handleResellerChange = async (newResellerId: string) => {
    setResellerId(newResellerId);
    // Clear contact fields when reseller changes
    setResellerContact('');
    setResellerContactEmail('');
    setResellerContactPhone('');
    loadContacts(newResellerId);
    if (!quote) return;
    setSaveStatus('saving');
    try {
      const data = await updateQuote(quote.id, {
        resellerId: newResellerId || null,
        resellerContact: '',
        resellerContactEmail: '',
        resellerContactPhone: '',
      });
      setQuote(data.quote);
      setResellerDiscountPct(pct(data.quote.resellerDiscount));
      setSaveStatus('saved');
    } catch {
      setSaveStatus('unsaved');
    }
  };

  const handleContactSelect = (contact: ResellerContact) => {
    setResellerContact(contact.name);
    setResellerContactEmail(contact.email ?? '');
    setResellerContactPhone(contact.phone ?? '');
    setShowContactDropdown(false);
    if (!quote) return;
    setSaveStatus('saving');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const data = await updateQuote(quote.id, {
          resellerContact: contact.name,
          resellerContactEmail: contact.email ?? '',
          resellerContactPhone: contact.phone ?? '',
        });
        setQuote(data.quote);
        setSaveStatus('saved');
      } catch {
        setSaveStatus('unsaved');
      }
    }, 300);
  };

  // ── Line item actions ────────────────────────────────────────────────────

  const handleAddProducts = async (productIds: string[]) => {
    if (!quote || productIds.length === 0) return;
    setSaveStatus('saving');
    try {
      await addLineItems(
        quote.id,
        productIds.map((pid) => ({ productId: pid })),
      );
      await loadQuote(quote.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add products');
      setSaveStatus('unsaved');
    }
  };

  const handleAddDescriptionRow = async () => {
    if (!quote) return;
    setSaveStatus('saving');
    try {
      await addLineItems(quote.id, [{ isDescriptionRow: true, descriptionText: '' }]);
      await loadQuote(quote.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add row');
      setSaveStatus('unsaved');
    }
  };

  const handleUpdateItem = useCallback(
    async (lineItemId: string, data: Record<string, unknown>) => {
      if (!quote) return;
      // Optimistic: update local state
      setQuote((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          lineItems: prev.lineItems.map((item) =>
            item.id === lineItemId ? { ...item, ...data } as QuoteLineItem : item,
          ),
        };
      });
      try {
        await updateLineItem(quote.id, lineItemId, data as { quantity?: number; projectDiscountOverride?: number | null; descriptionText?: string });
        setSaveStatus('saved');
      } catch {
        // Revert on failure
        await loadQuote(quote.id);
        setSaveStatus('unsaved');
      }
    },
    [quote, loadQuote],
  );

  const handleDeleteItem = useCallback(
    async (lineItemId: string) => {
      if (!quote) return;
      setQuote((prev) => {
        if (!prev) return prev;
        return { ...prev, lineItems: prev.lineItems.filter((i) => i.id !== lineItemId) };
      });
      try {
        await deleteLineItem(quote.id, lineItemId);
        setSaveStatus('saved');
      } catch {
        await loadQuote(quote.id);
        setSaveStatus('unsaved');
      }
    },
    [quote, loadQuote],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !quote) return;

      const items = [...quote.lineItems];
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const [moved] = items.splice(oldIndex, 1);
      items.splice(newIndex, 0, moved!);

      setQuote((prev) => (prev ? { ...prev, lineItems: items } : prev));

      try {
        await reorderLineItems(
          quote.id,
          items.map((i) => i.id),
        );
        setSaveStatus('saved');
      } catch {
        await loadQuote(quote.id);
        setSaveStatus('unsaved');
      }
    },
    [quote, loadQuote],
  );

  const handleRefreshPrices = async () => {
    if (!quote) return;
    setSaveStatus('saving');
    try {
      const result = await refreshQuotePrices(quote.id);
      setRefreshResult(result);
      if (result.updated > 0) {
        await loadQuote(quote.id);
      }
      setSaveStatus('saved');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to refresh prices');
      setSaveStatus('unsaved');
    }
  };

  // ── Create new reseller from quote ──────────────────────────────────────

  const handleCreateReseller = async () => {
    if (!newResellerName.trim()) return;
    setNewResellerSaving(true);
    setNewResellerError('');
    const discountPct = parseFloat(newResellerDiscount);
    if (isNaN(discountPct) || discountPct < 0 || discountPct > 100) {
      setNewResellerError('Discount must be between 0 and 100');
      setNewResellerSaving(false);
      return;
    }
    try {
      const data = await createReseller({
        name: newResellerName.trim(),
        defaultDiscount: discountPct / 100,
      });
      setResellers((prev) => [...prev, data.reseller].sort((a, b) => a.name.localeCompare(b.name)));
      setShowNewResellerModal(false);
      setNewResellerName('');
      setNewResellerDiscount('0');
      handleResellerChange(data.reseller.id);
    } catch (err) {
      setNewResellerError(err instanceof ApiError ? err.message : 'Failed to create reseller');
    } finally {
      setNewResellerSaving(false);
    }
  };

  // ── PDF download ──────────────────────────────────────────────────────

  const [showPdfDialog, setShowPdfDialog] = useState(false);

  const handleDownloadPdf = async () => {
    if (!quote) return;
    if (status === 'final') {
      await downloadPdf('FINAL');
      return;
    }
    setShowPdfDialog(true);
  };

  const handlePdfDialogChoice = async (finalize: boolean) => {
    setShowPdfDialog(false);
    if (!quote) return;
    if (finalize) {
      setSaveStatus('saving');
      try {
        const data = await updateQuote(quote.id, { status: 'final' });
        setQuote(data.quote);
        setStatus(data.quote.status);
        setSaveStatus('saved');
        await downloadPdf('FINAL');
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to finalize quote');
        setSaveStatus('unsaved');
      }
    } else {
      const pdfStatus = status === 'draft' ? 'DRAFT' : 'NOT FINAL';
      await downloadPdf(pdfStatus);
    }
  };

  const downloadPdf = async (pdfStatus: string) => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/quotes/${quote!.id}/pdf?pdfStatus=${encodeURIComponent(pdfStatus)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Quote-${quote!.displayId || quote!.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to generate PDF');
    }
    setPdfLoading(false);
  };

  // ── Share link ──────────────────────────────────────────────────────

  const handleCreateShareLink = async () => {
    if (!quote) return;
    setShareLinkCreating(true);
    try {
      const expiresInDays = parseInt(shareLinkExpiry, 10);
      const data = await createShareLink({
        quoteId: quote.id,
        recipientName: shareLinkName.trim() || undefined,
        recipientEmail: shareLinkEmail.trim() || undefined,
        expiresInDays: isNaN(expiresInDays) ? undefined : expiresInDays,
      });
      setShareLinks((prev) => [data.link, ...prev]);
      setShareLinkName('');
      setShareLinkEmail('');
      setShareLinkExpiry('30');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create share link');
    } finally {
      setShareLinkCreating(false);
    }
  };

  const copyShareUrl = (token: string, linkId: string) => {
    const url = `${window.location.origin}/share/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedLinkId(linkId);
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  // ── Attachments ────────────────────────────────────────────────────

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !quote) return;
    setUploading(true);
    try {
      const result = await uploadAttachment(quote.id, file);
      setAttachments((prev) => [...prev, result.attachment]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload failed');
    }
    setUploading(false);
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      await deleteAttachment(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to remove attachment');
    }
  };

  // ── Share dialog helpers ──────────────────────────────────────────────────

  /** Parse multi-email input: supports semicolons, commas, and "Name <email>" */
  const parseEmailInput = (raw: string): { email: string; name?: string }[] => {
    return raw
      .split(/[;,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((entry) => {
        const angleMatch = entry.match(/^(.+?)\s*<([^>]+)>$/);
        if (angleMatch) {
          return { email: angleMatch[2]!.trim(), name: angleMatch[1]!.trim() };
        }
        return { email: entry };
      });
  };

  const openShareDialog = async (tab: 'email' | 'sms' | 'link' = 'email') => {
    if (!quote) return;
    setShowShareDialog(true);
    setShareTab(tab);
    // Pre-populate from quote header
    setEmailTo(quote.resellerContactEmail || '');
    setEmailName(quote.resellerContact || '');
    setEmailSubject('');
    setEmailMessage('');
    setEmailIncludePdf(true);
    setEmailCollateralIds([]);
    setSmsPhone(quote.resellerContactPhone || '');
    setSmsError('');
    // Load collateral library + email history in parallel
    try {
      const [colRes, logRes] = await Promise.all([
        getCollateralItems(),
        getQuoteEmailLog(quote.id),
      ]);
      setCollateralLibrary(colRes.items);
      setEmailLog(logRes.emails);
      const defaults = colRes.items.filter((c) => c.isDefault).map((c) => c.id);
      setEmailCollateralIds(defaults);
    } catch {
      // non-critical
    }
    // Load existing share links for link tab
    try {
      const linksRes = await getShareLinks(quote.id);
      setShareLinks(linksRes.links);
    } catch {
      // non-critical
    }
  };

  const handleSendEmail = async () => {
    if (!quote || !emailTo) return;
    const recipients = parseEmailInput(emailTo);
    if (recipients.length === 0) return;
    setEmailSending(true);
    try {
      for (const r of recipients) {
        await emailQuote({
          quoteId: quote.id,
          recipientEmail: r.email,
          recipientName: r.name || emailName || undefined,
          subject: emailSubject || undefined,
          personalMessage: emailMessage || undefined,
          includePdf: emailIncludePdf,
          collateralItemIds: emailCollateralIds,
        });
      }
      const msg =
        recipients.length === 1
          ? `Quote emailed to ${recipients[0]!.email}`
          : `Quote shared to ${recipients.length} recipients`;
      setSuccess(msg);
      setShowShareDialog(false);
      setStatus('final');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleSendSms = async () => {
    if (!quote || !smsPhone) return;
    setSmsSending(true);
    setSmsError('');
    try {
      const result = await smsQuote({ quoteId: quote.id, recipientPhone: smsPhone });
      if (result.success) {
        setSuccess('Quote sent via text message');
        setShowShareDialog(false);
        setStatus('final');
      } else {
        setSmsError(
          result.error || 'Failed to send text message. A share link was still created.',
        );
      }
    } catch (err) {
      setSmsError(err instanceof ApiError ? err.message : 'Failed to send text message');
    } finally {
      setSmsSending(false);
    }
  };

  const toggleEmailCollateral = (id: string) => {
    setEmailCollateralIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Auto-dismiss success message
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  // Close reseller dropdown on outside click
  useEffect(() => {
    if (!showResellerDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (resellerDropdownRef.current && !resellerDropdownRef.current.contains(e.target as Node)) {
        setShowResellerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showResellerDropdown]);

  // ── Computed totals (client-side) ────────────────────────────────────────

  const resellerDiscount = parseFloat(resellerDiscountPct) / 100;
  const quoteProjectDiscount = parseFloat(projectDiscountPct) / 100;
  const targetMargin = targetMarginPct ? parseFloat(targetMarginPct) / 100 : 0;

  let totalMsrp = 0;
  let totalReseller = 0;
  let totalCost = 0;
  let totalSell = 0;
  let discountedItemCount = 0;
  let totalProjDiscPct = 0;

  if (quote) {
    for (const item of quote.lineItems) {
      if (item.isDescriptionRow) continue;
      const unitPrice = parseFloat(item.unitPrice ?? '0');
      const qty = item.quantity ?? 1;
      totalMsrp += unitPrice * qty;

      let costEach: number;
      if (isReseller) {
        // Reseller: per-line catalog discount (no project discount)
        const lineDiscount = parseFloat(item.discountSnapshot ?? '0');
        costEach = unitPrice * (1 - lineDiscount);
      } else {
        // Manufacturer: quote-level reseller + project discounts
        const applyProj = item.applyProjectDiscounts !== false;
        const effProj = applyProj
          ? (item.projectDiscountOverride !== null
              ? parseFloat(item.projectDiscountOverride)
              : quoteProjectDiscount)
          : 0;

        if (applyProj) {
          discountedItemCount++;
          totalProjDiscPct += effProj;
        }

        costEach = unitPrice * (1 - resellerDiscount) * (1 - effProj);
      }

      totalReseller += costEach * qty;
      totalCost += costEach * qty;

      const effMargin = item.marginOverride !== null ? parseFloat(item.marginOverride) : targetMargin;
      const sellEach = effMargin > 0 && effMargin < 1 ? costEach / (1 - effMargin) : costEach;
      totalSell += sellEach * qty;
    }
  }

  const avgProjectDiscount = discountedItemCount > 0 ? totalProjDiscPct / discountedItemCount : 0;
  const totalResellerBefore = totalMsrp * (1 - resellerDiscount);
  const hasProjectDiscounts = avgProjectDiscount > 0;
  const weightedMargin = totalSell > 0 ? 1 - totalCost / totalSell : 0;

  const isDraft = status === 'draft';
  const isReadOnly = status === 'ready' || status === 'final';

  const handleStatusChange = async (newValue: string) => {
    if (!quote) return;
    if (newValue === 'finalize') {
      setShowFinalizeConfirm(true);
      return;
    }
    setSaveStatus('saving');
    try {
      const data = await updateQuote(quote.id, { status: newValue });
      setQuote(data.quote);
      setStatus(data.quote.status);
      setSaveStatus('saved');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update status');
      setSaveStatus('unsaved');
    }
  };

  const confirmFinalize = async () => {
    if (!quote) return;
    setShowFinalizeConfirm(false);
    setSaveStatus('saving');
    try {
      const data = await updateQuote(quote.id, { status: 'final' });
      setQuote(data.quote);
      setStatus(data.quote.status);
      setSaveStatus('saved');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to finalize quote');
      setSaveStatus('unsaved');
    }
  };

  const handleToggleArchive = async () => {
    if (!quote) return;
    const newArchived = !archived;
    setSaveStatus('saving');
    try {
      const data = await updateQuote(quote.id, { archived: newArchived });
      setQuote(data.quote);
      setArchived(data.quote.archived);
      setSaveStatus('saved');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update archive status');
      setSaveStatus('unsaved');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="animate-fade-in">
        <button
          onClick={() => navigate('/quotes')}
          className="mb-4 inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Quotes
        </button>
        <div className="rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      </div>
    );
  }

  const lightInput =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';
  const lightLabel = 'mb-1 block text-sm font-medium text-gray-700';

  const selectedReseller = resellers.find((r) => r.id === resellerId);
  const filteredContacts = contactIsSearching
    ? resellerContactsList.filter(
        (c) => !resellerContact || c.name.toLowerCase().includes(resellerContact.toLowerCase()),
      )
    : resellerContactsList;

  return (
    <div className="animate-fade-in">
      {/* Back link */}
      <button
        onClick={() => navigate('/quotes')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Quotes
      </button>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      )}

      {/* Quote form container — dark page background */}
      <div className="mx-auto max-w-[1100px] rounded-lg bg-gray-800 p-6 shadow-lg">
        {/* Header row: Quote ID + Status + Save indicator */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="rounded bg-gray-700 px-3 py-1 text-sm font-semibold text-gray-200 shadow-sm">
              {quote?.displayId ?? 'New Quote'}
            </span>
            {quote && (
              <>
                {status !== 'final' ? (
                  <StatusSelector status={status} onChange={handleStatusChange} />
                ) : (
                  <div className="flex items-center gap-1.5 rounded-md border border-green-700 bg-green-900/20 px-3 py-1">
                    <Lock className="h-3.5 w-3.5 text-green-400" />
                    <span className="text-sm font-semibold text-green-400">Final</span>
                  </div>
                )}
                <select
                  value={visibility}
                  onChange={(e) => {
                    setVisibility(e.target.value);
                    saveHeaderField('visibility', e.target.value);
                  }}
                  disabled={status === 'final'}
                  className="rounded-md border border-gray-600 bg-gray-700 px-2 py-1 text-sm text-gray-200 focus:border-brand-500 focus:outline-none disabled:opacity-50"
                >
                  <option value="private">Private</option>
                  <option value="team">Team</option>
                </select>
                <button
                  onClick={handleToggleArchive}
                  className={`flex items-center gap-1 rounded-md border px-3 py-1 text-sm font-medium ${
                    archived
                      ? 'border-amber-700 bg-amber-900/30 text-amber-400 hover:bg-amber-900/50'
                      : 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
                  title={archived ? 'Unarchive' : 'Archive'}
                >
                  {archived ? (
                    <><ArchiveRestore className="h-3.5 w-3.5" /> Unarchive</>
                  ) : (
                    <><Archive className="h-3.5 w-3.5" /> Archive</>
                  )}
                </button>
              </>
            )}
          </div>
          <span
            className={`text-xs ${
              saveStatus === 'saved'
                ? 'text-green-400'
                : saveStatus === 'saving'
                  ? 'text-yellow-400'
                  : 'text-red-400'
            }`}
          >
            {saveStatus === 'saved'
              ? 'All changes saved'
              : saveStatus === 'saving'
                ? 'Saving...'
                : 'Unsaved changes'}
          </span>
        </div>

        {/* Read-only banner for Ready/Final quotes */}
        {isReadOnly && (
          <div className={`mb-4 flex items-center gap-2 rounded-lg px-4 py-2 text-sm ${
            status === 'final'
              ? 'border border-green-800 bg-green-900/20 text-green-400'
              : 'border border-amber-800 bg-amber-900/20 text-amber-400'
          }`}>
            <Lock className="h-4 w-4" />
            {status === 'final'
              ? 'This quote is finalized and cannot be edited. Duplicate to make changes.'
              : 'This quote is in Ready status. Change to Draft to edit.'}
          </div>
        )}

        {/* Sent-to info bar (manufacturer only) */}
        {isManufacturer && sentRecipients.length > 0 && (
          <div className="mb-4 rounded-lg border border-blue-800 bg-blue-900/20 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-400">
              <Users className="h-4 w-4" />
              Sent to {sentRecipients.length} reseller{sentRecipients.length !== 1 ? 's' : ''}
            </div>
            <div className="mt-2 space-y-1">
              {sentRecipients.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-xs text-blue-300/80">
                  <span>{r.recipientTenantName}</span>
                  <span className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" /> {r.viewCount} view{r.viewCount !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Download className="h-3 w-3" /> {r.downloadCount} download{r.downloadCount !== 1 ? 's' : ''}
                    </span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                      r.status === 'downloaded'
                        ? 'bg-green-900/40 text-green-400'
                        : r.status === 'viewed'
                          ? 'bg-gray-700 text-gray-400'
                          : 'bg-blue-900/40 text-blue-400'
                    }`}>
                      {r.status}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quote metadata section — tool area, not printed */}
        <div className="mb-4 rounded-lg border border-gray-300 bg-gray-200 p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Left column — project + reseller */}
            <div className="space-y-4">
              <div>
                <label className={lightLabel}>Project Name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  onBlur={() => saveHeaderField('projectName', projectName)}
                  className={lightInput}
                  placeholder="Enter project name..."
                  disabled={isReadOnly}
                />
              </div>
              <div className="relative" ref={resellerDropdownRef}>
                <label className={lightLabel}>{labels.Reseller}</label>
                <button
                  type="button"
                  onClick={() => !isReadOnly && setShowResellerDropdown(!showResellerDropdown)}
                  disabled={isReadOnly}
                  className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-60"
                >
                  <span className={selectedReseller ? 'text-gray-900' : 'text-gray-500'}>
                    {selectedReseller
                      ? parseFloat(String(selectedReseller.defaultDiscount)) > 0
                        ? `${selectedReseller.name} (${pct(selectedReseller.defaultDiscount)}%)`
                        : selectedReseller.name
                      : `No ${labels.Reseller}`}
                  </span>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>
                {showResellerDropdown && (
                  <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        handleResellerChange('');
                        setShowResellerDropdown(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-brand-50 ${
                        !resellerId ? 'bg-brand-50 text-brand-700' : 'text-gray-900'
                      }`}
                    >
                      No {labels.Reseller}
                    </button>
                    {resellers.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => {
                          handleResellerChange(r.id);
                          setShowResellerDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-brand-50 ${
                          resellerId === r.id ? 'bg-brand-50 text-brand-700' : 'text-gray-900'
                        }`}
                      >
                        {r.name}{parseFloat(String(r.defaultDiscount)) > 0 ? ` (${pct(r.defaultDiscount)}%)` : ''}
                      </button>
                    ))}
                    <div className="border-t border-gray-200" />
                    <button
                      type="button"
                      onClick={() => {
                        setShowResellerDropdown(false);
                        setShowNewResellerModal(true);
                        setNewResellerName('');
                        setNewResellerDiscount('0');
                        setNewResellerError('');
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-brand-600 hover:bg-brand-50"
                    >
                      <Plus className="h-4 w-4" />
                      New {labels.Reseller}
                    </button>
                  </div>
                )}
              </div>
              <div className="relative">
                <label className={lightLabel}>{labels.Reseller} Contact</label>
                <input
                  type="text"
                  value={resellerContact}
                  onChange={(e) => {
                    setResellerContact(e.target.value);
                    setContactIsSearching(true);
                    setShowContactDropdown(true);
                  }}
                  onFocus={() => {
                    setContactIsSearching(false);
                    setShowContactDropdown(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowContactDropdown(false);
                      setContactIsSearching(false);
                      saveHeaderField('resellerContact', resellerContact);
                    }, 200);
                  }}
                  className={lightInput}
                  placeholder={resellerId ? 'Type or select contact...' : 'Select a reseller first'}
                  disabled={!resellerId || isReadOnly}
                />
                {showContactDropdown && resellerId && resellerContactsList.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                    {resellerContact && (
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setResellerContact('');
                          setResellerContactEmail('');
                          setResellerContactPhone('');
                          setShowContactDropdown(false);
                          saveHeaderField('resellerContact', '');
                          saveHeaderField('resellerContactEmail', '');
                          saveHeaderField('resellerContactPhone', '');
                        }}
                        className="flex w-full items-center gap-1 border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
                      >
                        <X className="h-3 w-3" />
                        Clear selection
                      </button>
                    )}
                    {filteredContacts.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleContactSelect(c)}
                        className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-gray-50"
                      >
                        <span className="font-medium text-gray-900">{c.name}</span>
                        {(c.email || c.phone) && (
                          <span className="text-xs text-gray-500">
                            {[c.email, c.phone ? formatPhone(c.phone) : null].filter(Boolean).join(' \u2022 ')}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lightLabel}>Contact Email</label>
                  <input
                    type="email"
                    value={resellerContactEmail}
                    onChange={(e) => {
                      setResellerContactEmail(e.target.value);
                      if (contactEmailError) setContactEmailError('');
                    }}
                    onBlur={() => {
                      if (resellerContactEmail && !isValidEmail(resellerContactEmail)) {
                        setContactEmailError('Please enter a valid email address');
                      } else {
                        setContactEmailError('');
                      }
                      saveHeaderField('resellerContactEmail', resellerContactEmail);
                    }}
                    className={`${lightInput} ${contactEmailError ? '!border-red-500' : ''}`}
                    placeholder="email@example.com"
                    disabled={!resellerId || isReadOnly}
                  />
                  {contactEmailError && (
                    <p className="mt-1 text-xs text-red-500">{contactEmailError}</p>
                  )}
                </div>
                <div>
                  <label className={lightLabel}>Contact Phone</label>
                  <input
                    type="text"
                    value={resellerContactPhone}
                    onChange={(e) => setResellerContactPhone(e.target.value)}
                    onBlur={() => {
                      if (resellerContactPhone) {
                        const formatted = formatPhone(resellerContactPhone);
                        setResellerContactPhone(formatted);
                        saveHeaderField('resellerContactPhone', formatted);
                      } else {
                        saveHeaderField('resellerContactPhone', resellerContactPhone);
                      }
                    }}
                    className={lightInput}
                    placeholder="(555) 123-4567"
                    disabled={!resellerId || isReadOnly}
                  />
                </div>
              </div>

              {/* Discount controls */}
              <div className="flex flex-wrap gap-4">
                {!isReseller && (
                  <>
                    <div>
                      <label className={lightLabel}>{labels.Reseller} Discount (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={resellerDiscountPct}
                        onChange={(e) => setResellerDiscountPct(e.target.value)}
                        onBlur={() => saveHeaderField('resellerDiscount', fromPct(resellerDiscountPct))}
                        className="w-28 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        disabled={isReadOnly}
                      />
                    </div>
                    <div>
                      <label className={lightLabel}>Project Discount (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={projectDiscountPct}
                        onChange={(e) => setProjectDiscountPct(e.target.value)}
                        onBlur={() => saveHeaderField('projectDiscount', fromPct(projectDiscountPct))}
                        className="w-28 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        disabled={isReadOnly}
                      />
                    </div>
                  </>
                )}
                {isReseller && (
                  <div>
                    <label className={lightLabel}>Target Margin (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="99.99"
                      step="0.1"
                      value={targetMarginPct}
                      onChange={(e) => setTargetMarginPct(e.target.value)}
                      onBlur={() => {
                        const val = targetMarginPct.trim() === '' ? null : fromPct(targetMarginPct);
                        saveHeaderField('targetMargin', val);
                      }}
                      className="w-28 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      placeholder="e.g. 25"
                      disabled={isReadOnly}
                    />
                  </div>
                )}
              </div>
              <div>
                <label className={lightLabel}>
                  Valid Until
                  <span className="ml-1 font-normal text-gray-400">
                    {validUntil
                      ? `(${getValidUntilHelper(validUntil)})`
                      : "(won't appear on PDF unless set)"}
                  </span>
                </label>
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => {
                    setValidUntil(e.target.value);
                    saveHeaderField('validUntil', e.target.value || null);
                  }}
                  className={lightInput}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            {/* Right column — Quoted By */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-600">Quoted By</label>
                <button
                  onClick={() => setEditingQuotedBy(!editingQuotedBy)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  <Pencil className="h-3 w-3" />
                  {editingQuotedBy ? 'Done' : 'Edit'}
                </button>
              </div>

              {editingQuotedBy ? (
                <div className="space-y-3">
                  <div>
                    <label className={lightLabel}>Name</label>
                    <input
                      type="text"
                      value={quotedBy}
                      onChange={(e) => setQuotedBy(e.target.value)}
                      onBlur={() => saveHeaderField('quotedBy', quotedBy)}
                      className={lightInput}
                    />
                  </div>
                  <div>
                    <label className={lightLabel}>Title / Role</label>
                    <textarea
                      value={quotedByTitle}
                      onChange={(e) => setQuotedByTitle(e.target.value)}
                      onBlur={() => saveHeaderField('quotedByTitle', quotedByTitle || null)}
                      className={lightInput}
                      rows={2}
                      placeholder={"e.g. Sales Engineer\nAcme Corporation"}
                    />
                  </div>
                  <div>
                    <label className={lightLabel}>Email</label>
                    <input
                      type="email"
                      value={quotedByEmail}
                      onChange={(e) => {
                        setQuotedByEmail(e.target.value);
                        if (quotedByEmailError) setQuotedByEmailError('');
                      }}
                      onBlur={() => {
                        if (quotedByEmail && !isValidEmail(quotedByEmail)) {
                          setQuotedByEmailError('Please enter a valid email address');
                        } else {
                          setQuotedByEmailError('');
                        }
                        saveHeaderField('quotedByEmail', quotedByEmail);
                      }}
                      className={`${lightInput} ${quotedByEmailError ? '!border-red-500' : ''}`}
                    />
                    {quotedByEmailError && (
                      <p className="mt-1 text-xs text-red-500">{quotedByEmailError}</p>
                    )}
                  </div>
                  <div>
                    <label className={lightLabel}>Phone</label>
                    <input
                      type="text"
                      value={quotedByPhone}
                      onChange={(e) => setQuotedByPhone(e.target.value)}
                      onBlur={() => {
                        if (quotedByPhone) {
                          const formatted = formatPhone(quotedByPhone);
                          setQuotedByPhone(formatted);
                          saveHeaderField('quotedByPhone', formatted);
                        } else {
                          saveHeaderField('quotedByPhone', quotedByPhone);
                        }
                      }}
                      className={lightInput}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-md bg-gray-100 px-4 py-3 text-sm">
                  <div className="font-medium text-gray-800">{quotedBy || '—'}</div>
                  {quotedByTitle && (
                    <div className="whitespace-pre-wrap text-gray-500">{quotedByTitle}</div>
                  )}
                  {quotedByEmail && (
                    <div className="text-gray-600">Email: {quotedByEmail}</div>
                  )}
                  {quotedByPhone && (
                    <div className="text-gray-600">Mobile: {formatPhone(quotedByPhone)}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action bar — tool controls */}
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border-y border-gray-300 bg-gray-200 px-6 py-3">
          <button
            onClick={() => setShowProductModal(true)}
            disabled={isReadOnly}
            className="flex items-center gap-2 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Add Products
          </button>
          <button
            onClick={handleAddDescriptionRow}
            disabled={isReadOnly}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <AlignLeft className="h-4 w-4" />
            Add Description Row
          </button>
          <button
            onClick={handleRefreshPrices}
            disabled={isReadOnly}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh Prices
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {pdfLoading ? 'Generating PDF...' : 'Download PDF'}
          </button>
          <button
            onClick={() => openShareDialog()}
            className="flex items-center gap-2 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            <Share2 className="h-4 w-4" />
            Share Quote
          </button>
        </div>

        {/* Success message */}
        {success && (
          <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {success}
          </div>
        )}

        {/* Refresh result notification */}
        {refreshResult && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {refreshResult.updated === 0 ? (
              'All prices are up to date.'
            ) : (
              <>
                Updated {refreshResult.updated} price{refreshResult.updated !== 1 ? 's' : ''}:
                <ul className="mt-1 list-inside list-disc">
                  {refreshResult.changes.map((c, i) => (
                    <li key={i}>
                      {c.partNumber}: {formatCurrency(c.oldPrice)} &rarr; {formatCurrency(c.newPrice)}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <button
              onClick={() => setRefreshResult(null)}
              className="mt-1 text-xs text-blue-600 underline hover:text-blue-800"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Line items table — printable content, white bg */}
        <div className="overflow-x-auto rounded-lg border border-gray-300 bg-white shadow-md">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-600">
                <th className="w-8 px-2 py-2" />
                <th className="w-8 px-2 py-2">#</th>
                {isReseller ? (
                  <>
                    <th className="px-2 py-2">Part Number</th>
                    <th className="px-2 py-2">Description</th>
                    <th className="w-16 px-2 py-2 text-center">Qty</th>
                    <th className="px-2 py-2 text-right">Your Cost</th>
                    <th className="w-20 px-2 py-2 text-center">Margin %</th>
                    <th className="px-2 py-2 text-right">Sell Ea.</th>
                    <th className="px-2 py-2 text-right">Sell Tot.</th>
                  </>
                ) : (
                  <>
                    <th className="px-2 py-2">Group</th>
                    <th className="px-2 py-2">Part Number</th>
                    <th className="px-2 py-2">Description</th>
                    <th className="w-16 px-2 py-2 text-center">Qty</th>
                    <th className="px-2 py-2 text-right">MSRP</th>
                    <th className="w-20 px-2 py-2 text-center">Discount</th>
                    <th className="px-2 py-2 text-right">{labels.Reseller} Ea.</th>
                    <th className="px-2 py-2 text-right">{labels.Reseller} Tot.</th>
                  </>
                )}
                <th className="w-8 px-2 py-2" />
              </tr>
            </thead>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={quote?.lineItems.map((i) => i.id) ?? []}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {quote?.lineItems.length === 0 && (
                    <tr>
                      <td colSpan={isReseller ? 10 : 11} className="py-8 text-center text-sm text-gray-400">
                        No line items yet. Click &ldquo;Add Products&rdquo; to get started.
                      </td>
                    </tr>
                  )}
                  {quote?.lineItems.map((item, idx) => (
                    <SortableRow
                      key={item.id}
                      item={item}
                      index={idx}
                      resellerDiscount={resellerDiscount}
                      quoteProjectDiscount={quoteProjectDiscount}
                      isReseller={!!isReseller}
                      targetMargin={targetMargin}
                      readOnly={isReadOnly}
                      onUpdateItem={handleUpdateItem}
                      onDeleteItem={handleDeleteItem}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </DndContext>
          </table>
        </div>

        {/* Totals — printable content, white bg */}
        <div className="mt-4 flex justify-end rounded-lg border border-gray-300 bg-white p-4 shadow-md">
          <div className="w-80 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Total MSRP:</span>
              <span className="font-medium text-gray-900">{formatCurrency(totalMsrp)}</span>
            </div>
            {isReseller ? (
              <>
                <div className="flex justify-between border-t border-gray-200 pt-1">
                  <span className="font-semibold text-gray-900">Your Cost:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(totalCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Sell Price:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(totalSell)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Weighted Margin:</span>
                  <span className={`font-semibold ${weightedMargin >= 0.2 ? 'text-green-700' : weightedMargin >= 0.1 ? 'text-amber-700' : 'text-red-700'}`}>
                    {pct(weightedMargin)}%
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">{labels.Reseller} Discount:</span>
                  <span className="text-gray-900">{resellerDiscountPct}%</span>
                </div>
                {hasProjectDiscounts && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pre-Project Cost:</span>
                      <span className="text-gray-900">{formatCurrency(totalResellerBefore)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Project Discount:</span>
                      <span className="text-gray-900">{pct(avgProjectDiscount)}%</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between border-t border-gray-200 pt-1">
                  <span className="font-semibold text-gray-900">Final {labels.Reseller} Cost:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(totalReseller)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Quote Notes — visible on PDF, open by default */}
        <div className="mt-4 overflow-hidden rounded-lg border border-gray-300 bg-gray-200">
          <button
            type="button"
            onClick={() => setQuoteNotesOpen(!quoteNotesOpen)}
            className="flex w-full items-center justify-between px-4 py-2 hover:bg-gray-300/50 transition-colors"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MessageSquare className="h-4 w-4" />
              Quote Notes
              <span className="text-xs text-gray-500">(visible on PDF)</span>
            </span>
            {quoteNotesOpen ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
          </button>
          {quoteNotesOpen && (
            <div className="px-4 py-3">
              <textarea
                value={quoteNotes}
                onChange={(e) => setQuoteNotes(e.target.value)}
                onBlur={() => saveHeaderField('quoteNotes', quoteNotes || null)}
                rows={3}
                className={lightInput}
                placeholder="Add notes visible to the quote recipient (e.g., scope details, special terms, delivery notes)..."
              />
            </div>
          )}
        </div>

        {/* Attachments — only for saved quotes */}
        {quote && id && (
          <div className="mt-3 mb-3">
            <button
              type="button"
              onClick={() => setShowAttachments(!showAttachments)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
            >
              <Paperclip className="h-4 w-4" />
              Attachments ({attachments.length})
              <span className="text-xs">{showAttachments ? '\u25BC' : '\u25B6'}</span>
            </button>

            {showAttachments && (
              <div className="mt-2 rounded-lg border border-gray-300 bg-white p-3">
                {attachments.length === 0 && isDraft && (
                  <p className="text-xs text-gray-400">No attachments yet.</p>
                )}
                {attachments.length === 0 && !isDraft && (
                  <p className="text-xs text-gray-400">No attachments</p>
                )}

                {attachments.map((att) => (
                  <div key={att.id} className="flex items-center justify-between border-b border-gray-100 py-2 last:border-0">
                    <a
                      href={getAttachmentDownloadUrl(att.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700"
                    >
                      <Paperclip className="h-3.5 w-3.5 text-gray-400" />
                      {att.label || att.originalFilename}
                      <span className="text-xs text-gray-400">
                        ({att.fileSize < 1024 * 1024
                          ? `${(att.fileSize / 1024).toFixed(0)} KB`
                          : `${(att.fileSize / (1024 * 1024)).toFixed(1)} MB`})
                      </span>
                    </a>
                    {isDraft && (
                      <button
                        type="button"
                        onClick={() => handleDeleteAttachment(att.id)}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                        title="Remove attachment"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}

                {isDraft && (
                  <div className={`${attachments.length > 0 ? 'mt-2 border-t border-gray-100 pt-2' : ''}`}>
                    <input
                      type="file"
                      ref={attachmentInputRef}
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.csv,.txt"
                      onChange={handleAttachmentUpload}
                    />
                    <button
                      type="button"
                      onClick={() => attachmentInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 disabled:opacity-50"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {uploading ? 'Uploading...' : 'Add Attachment'}
                    </button>
                    <p className="mt-1 text-xs text-gray-400">PDF, images, Word, Excel, CSV. Max 10MB.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Company Boilerplate / Disclaimer — collapsed by default */}
        {(boilerplateText || tenantSettings?.quoteBoilerplate) && (
          <div className="mt-4 overflow-hidden rounded-lg border border-gray-300 bg-gray-200">
            <button
              type="button"
              onClick={() => setBoilerplateOpen(!boilerplateOpen)}
              className="flex w-full items-center justify-between px-4 py-2 hover:bg-gray-300/50 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm text-gray-500">
                <FileText className="h-4 w-4" />
                Company Disclaimer / Boilerplate
                {boilerplateOverride && (
                  <span className="rounded bg-amber-200 px-1.5 py-0.5 text-xs text-amber-700">
                    Modified
                  </span>
                )}
              </span>
              {boilerplateOpen ? <ChevronUp className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
            </button>
            {boilerplateOpen && (
              <div className="px-4 py-3">
                {tenantSettings?.boilerplateEditable ? (
                  <textarea
                    value={boilerplateText ?? tenantSettings?.quoteBoilerplate ?? ''}
                    onChange={(e) => {
                      setBoilerplateText(e.target.value);
                      setBoilerplateOverride(true);
                    }}
                    onBlur={() => saveHeaderField('boilerplateText', boilerplateText)}
                    rows={4}
                    className="w-full rounded-md border border-gray-300 bg-white/70 px-3 py-2 text-xs text-gray-600 leading-relaxed focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                ) : (
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {tenantSettings?.quoteBoilerplate}
                  </p>
                )}
                {boilerplateOverride && tenantSettings?.boilerplateEditable && (
                  <button
                    type="button"
                    onClick={() => {
                      setBoilerplateText(null);
                      setBoilerplateOverride(false);
                      saveHeaderField('boilerplateText', null);
                      saveHeaderField('boilerplateOverride', false);
                    }}
                    className="mt-1 text-xs text-brand-600 hover:text-brand-700"
                  >
                    Reset to company default
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Internal Notes — tool area, NOT on PDF */}
        <div className="mt-4 rounded-lg border border-gray-300 bg-gray-200 p-4">
          <label className={lightLabel}>Internal Notes <span className="font-normal text-gray-500">(hidden from PDF)</span></label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => saveHeaderField('notes', notes)}
            rows={3}
            className={lightInput}
            placeholder="Internal notes about this quote (not visible on PDF)..."
          />
        </div>
      </div>

      {/* Product Search Modal (dark theme overlay) */}
      <ProductSearchModal
        open={showProductModal}
        onClose={() => setShowProductModal(false)}
        onAdd={handleAddProducts}
      />

      {/* New Reseller Modal */}
      {/* Finalize Confirmation Modal */}
      {showFinalizeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-900/30">
                <Lock className="h-5 w-5 text-green-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-100">Finalize Quote</h2>
            </div>
            <p className="mb-6 text-sm text-gray-400">
              Finalizing locks this quote so it can be confidently shared or exported as a
              finished document. The quote will no longer be editable after this step.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowFinalizeConfirm(false)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmFinalize}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                Finalize
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Download Dialog */}
      {showPdfDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md rounded-lg bg-gray-800 p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-100">Download PDF</h3>
            <p className="mt-2 text-sm text-gray-400">
              Choose how you'd like to download this quote.
            </p>
            <div className="mt-3 space-y-1 text-xs text-gray-500">
              <p>
                {status === 'draft'
                  ? 'Download as Draft — PDF will show a Draft status.'
                  : 'Download as Not Final — PDF will show a Not Final status.'}
              </p>
              <p>Download as Final — PDF will show Final status and the quote becomes immutable.</p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowPdfDialog(false)}
                className="rounded-md px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePdfDialogChoice(false)}
                className="rounded-md border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-600"
              >
                {status === 'draft' ? 'Download as Draft' : 'Download as Not Final'}
              </button>
              <button
                onClick={() => handlePdfDialogChoice(true)}
                className="rounded-md border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 hover:bg-gray-600"
              >
                Download as Final
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewResellerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-100">New {labels.Reseller}</h2>

            {newResellerError && (
              <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">
                {newResellerError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="new-reseller-name" className="mb-1 block text-sm text-gray-400">
                  Name *
                </label>
                <input
                  id="new-reseller-name"
                  type="text"
                  value={newResellerName}
                  onChange={(e) => setNewResellerName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateReseller();
                  }}
                  autoFocus
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="e.g. ABC Distribution"
                />
              </div>
              <div>
                <label htmlFor="new-reseller-discount" className="mb-1 block text-sm text-gray-400">
                  Default Discount (%)
                </label>
                <input
                  id="new-reseller-discount"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={newResellerDiscount}
                  onChange={(e) => setNewResellerDiscount(e.target.value)}
                  className="w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="0"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewResellerModal(false)}
                  className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateReseller}
                  disabled={!newResellerName.trim() || newResellerSaving}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {newResellerSaving ? 'Creating...' : `Create ${labels.Reseller}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Combined Share Quote Dialog */}
      {showShareDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg rounded-xl border border-gray-800 bg-gray-900 p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-100">
                <Share2 className="h-5 w-5" />
                Share Quote
              </h3>
              <button
                onClick={() => setShowShareDialog(false)}
                className="text-gray-500 hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tab bar */}
            <div className="mb-4 flex gap-1 rounded-lg bg-gray-800 p-1">
              {([
                { key: 'email' as const, label: 'Email', icon: Mail },
                { key: 'sms' as const, label: 'Text', icon: Smartphone },
                { key: 'link' as const, label: 'Copy Link', icon: Link },
              ]).map((t) => (
                <button
                  key={t.key}
                  onClick={() => setShareTab(t.key)}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    shareTab === t.key
                      ? 'bg-brand-600 text-white'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>

            {/* ── Email tab ── */}
            {shareTab === 'email' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-gray-400">
                      Recipient Email(s) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      placeholder="email@example.com; another@example.com"
                      className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-400">Recipient Name</label>
                    <input
                      type="text"
                      value={emailName}
                      onChange={(e) => setEmailName(e.target.value)}
                      placeholder="Optional"
                      className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-xs text-gray-400">Subject</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder={`Quote${quote?.projectName ? ` for ${quote.projectName}` : ''} from ${user?.tenant?.name || 'us'}`}
                    className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs text-gray-400">Personal Message</label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Add a personal note to the email..."
                    rows={3}
                    className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="emailIncludePdf"
                    checked={emailIncludePdf}
                    onChange={(e) => setEmailIncludePdf(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-brand-600 focus:ring-brand-500"
                  />
                  <label htmlFor="emailIncludePdf" className="text-sm text-gray-300">
                    Attach PDF to email
                  </label>
                </div>

                {collateralLibrary.length > 0 && (
                  <div>
                    <label className="mb-2 block text-xs text-gray-400">Include Documents</label>
                    <div className="max-h-32 space-y-1 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800 p-2">
                      {collateralLibrary.map((item) => (
                        <label
                          key={item.id}
                          className="flex items-center gap-2 rounded px-2 py-1 hover:bg-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={emailCollateralIds.includes(item.id)}
                            onChange={() => toggleEmailCollateral(item.id)}
                            className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-brand-600 focus:ring-brand-500"
                          />
                          <span className="text-sm text-gray-300">
                            {item.label || item.originalFilename}
                          </span>
                          {item.category && (
                            <span className="rounded bg-gray-600 px-1.5 py-0.5 text-[10px] text-gray-400">
                              {item.category}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {emailLog.length > 0 && (
                  <div>
                    <label className="mb-1 block text-xs text-gray-400">Previously Sent</label>
                    <div className="max-h-24 space-y-1 overflow-y-auto text-xs text-gray-500">
                      {emailLog.map((entry) => (
                        <div key={entry.id} className="flex justify-between">
                          <span>
                            {entry.recipientEmail}
                            {entry.recipientName ? ` (${entry.recipientName})` : ''}
                          </span>
                          <span>{new Date(entry.sentAt).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex justify-end gap-3">
                  <button
                    onClick={() => setShowShareDialog(false)}
                    className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={emailSending || !emailTo}
                    className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    <Mail className="h-4 w-4" />
                    {emailSending ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>
            )}

            {/* ── SMS tab ── */}
            {shareTab === 'sms' && (
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs text-gray-400">
                    Phone Number <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="tel"
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value)}
                    onBlur={(e) => setSmsPhone(formatPhone(e.target.value))}
                    placeholder="(555) 123-4567"
                    className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                  />
                </div>

                <div className="rounded-lg border border-gray-700 bg-gray-800 p-3">
                  <div className="mb-1 text-xs text-gray-400">Message Preview</div>
                  <p className="text-sm text-gray-300">
                    {user?.name || 'You'} from {user?.tenant?.name || 'your company'} sent you a
                    quote{quote?.projectName ? ` for ${quote.projectName}` : ''}. View: [link]
                  </p>
                </div>

                {smsError && (
                  <div className="rounded-lg border border-amber-700 bg-amber-900/30 px-3 py-2 text-sm text-amber-300">
                    {smsError}
                  </div>
                )}

                <div className="mt-4 flex justify-end gap-3">
                  <button
                    onClick={() => setShowShareDialog(false)}
                    className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendSms}
                    disabled={smsSending || !smsPhone}
                    className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    <Smartphone className="h-4 w-4" />
                    {smsSending ? 'Sending...' : 'Send Text'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Copy Link tab ── */}
            {shareTab === 'link' && (
              <div>
                <div className="mb-4 space-y-3 rounded-lg border border-gray-700 bg-gray-800 p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs text-gray-400">Recipient Name</label>
                      <input
                        type="text"
                        value={shareLinkName}
                        onChange={(e) => setShareLinkName(e.target.value)}
                        placeholder="Optional"
                        className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-gray-400">Recipient Email</label>
                      <input
                        type="email"
                        value={shareLinkEmail}
                        onChange={(e) => setShareLinkEmail(e.target.value)}
                        placeholder="Optional"
                        className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="w-32">
                      <label className="mb-1 block text-xs text-gray-400">Expires (days)</label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={shareLinkExpiry}
                        onChange={(e) => setShareLinkExpiry(e.target.value)}
                        className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
                      />
                    </div>
                    <button
                      onClick={handleCreateShareLink}
                      disabled={shareLinkCreating}
                      className="flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                    >
                      {shareLinkCreating ? 'Creating...' : 'Create Link'}
                    </button>
                  </div>
                </div>

                {shareLinks.length > 0 && (
                  <div className="max-h-60 space-y-2 overflow-y-auto">
                    <div className="text-xs font-medium uppercase text-gray-500">
                      Existing Links
                    </div>
                    {shareLinks.map((link) => {
                      const url = `${window.location.origin}/share/${link.shareToken}`;
                      const isExpired =
                        link.expiresAt && new Date(link.expiresAt) < new Date();
                      return (
                        <div
                          key={link.id}
                          className={`rounded-lg border p-3 ${
                            isExpired || !link.isActive
                              ? 'border-gray-700 bg-gray-800/50 opacity-60'
                              : 'border-gray-700 bg-gray-800'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                {link.recipientName && (
                                  <span className="text-sm font-medium text-gray-200">
                                    {link.recipientName}
                                  </span>
                                )}
                                {link.recipientEmail && (
                                  <span className="text-xs text-gray-500">
                                    {link.recipientEmail}
                                  </span>
                                )}
                                {!link.recipientName && !link.recipientEmail && (
                                  <span className="text-sm text-gray-400">Anonymous link</span>
                                )}
                                {(isExpired || !link.isActive) && (
                                  <span className="rounded bg-red-900/40 px-1.5 py-0.5 text-[10px] font-medium text-red-400">
                                    {isExpired ? 'Expired' : 'Inactive'}
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Eye className="h-3 w-3" /> {link.viewCount}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Download className="h-3 w-3" /> {link.downloadCount}
                                </span>
                                {link.expiresAt && (
                                  <span>
                                    Expires: {new Date(link.expiresAt).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => copyShareUrl(link.shareToken, link.id)}
                              className="ml-3 flex items-center gap-1 rounded-md border border-gray-600 px-2 py-1 text-xs text-gray-400 hover:border-gray-500 hover:text-gray-200"
                              title="Copy link"
                            >
                              <Copy className="h-3 w-3" />
                              {copiedLinkId === link.id ? 'Copied!' : 'Copy'}
                            </button>
                          </div>
                          {!(isExpired || !link.isActive) && (
                            <div className="mt-2">
                              <input
                                readOnly
                                value={url}
                                className="w-full rounded border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-gray-400"
                                onFocus={(e) => e.target.select()}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {shareLinks.length === 0 && (
                  <p className="text-center text-sm text-gray-500">
                    No share links yet. Create one above.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
